import type { LivingUniverseCore } from "./living-universe-core";
import type { NexusEvent, NexusSnapshot } from "./nexus";

export interface BrowserNexusFederation {
  stop(): void;
  sync(): number;
}

const DEFAULT_KEY = "inyron:living-nexus:v1";
const DEFAULT_CHANNEL = "inyron-living-nexus";
const MAX_SHARED_EVENTS = 512;
const MAX_PENDING_EVENTS = 2048;
const FLUSH_INTERVAL_MS = 150;

function validEvent(value: unknown): value is NexusEvent {
  if (!value || typeof value !== "object") return false;
  const event = value as Partial<NexusEvent>;
  return typeof event.id === "string"
    && typeof event.atMs === "number"
    && typeof event.gameId === "string"
    && typeof event.worldId === "string"
    && typeof event.type === "string"
    && typeof event.source === "string";
}

export function installBrowserNexusFederation(
  core: LivingUniverseCore,
  options: { storageKey?: string; channelName?: string } = {},
): BrowserNexusFederation {
  if (typeof window === "undefined") return { stop() {}, sync: () => 0 };

  const storageKey = options.storageKey ?? DEFAULT_KEY;
  const channelName = options.channelName ?? DEFAULT_CHANNEL;
  const seen = new Set<string>();
  const pending: NexusEvent[] = [];
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let channel: BroadcastChannel | null = null;
  let stopped = false;

  const markSeen = (id: string): boolean => {
    if (seen.has(id)) return false;
    seen.add(id);
    if (seen.size > 8192) {
      for (const oldId of seen) {
        seen.delete(oldId);
        if (seen.size <= 4096) break;
      }
    }
    return true;
  };

  const readShared = (): NexusEvent[] => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Partial<NexusSnapshot>;
      if (!Array.isArray(parsed.events)) return [];
      return parsed.events.filter(validEvent).slice(-MAX_SHARED_EVENTS);
    } catch {
      return [];
    }
  };

  const flush = (): void => {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (pending.length === 0) return;
    const batch = pending.splice(0);
    const tail = batch.slice(-MAX_SHARED_EVENTS);
    const merged = new Map<string, NexusEvent>();
    for (const event of readShared()) merged.set(event.id, event);
    for (const event of tail) merged.set(event.id, event);
    const recent = [...merged.values()]
      .sort((a, b) => a.atMs - b.atMs || a.id.localeCompare(b.id))
      .slice(-MAX_SHARED_EVENTS);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ version: 1, events: recent }));
    } catch {
      // Shared persistence is best-effort; local game snapshots remain authoritative.
    }
    try { channel?.postMessage(tail.length === 1 ? tail[0] : tail); }
    catch { /* channel closing or unsupported */ }
  };

  const writeShared = (event: NexusEvent): void => {
    if (stopped || !markSeen(event.id)) return;
    pending.push(event);
    if (pending.length > MAX_PENDING_EVENTS) {
      pending.splice(0, pending.length - MAX_SHARED_EVENTS);
    }
    if (event.type === "fight-match-complete" || event.type === "offline-catchup") {
      flush();
    } else if (flushTimer === null) {
      flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
  };

  const ingestShared = (): number => {
    let ingested = 0;
    for (const event of readShared()) {
      markSeen(event.id);
      if (event.worldId !== core.worldId && core.nexus.ingest(event)) ingested += 1;
    }
    return ingested;
  };

  const unsubscribe = core.nexus.subscribe(event => writeShared(event));
  ingestShared();

  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(channelName);
    channel.onmessage = message => {
      const events = Array.isArray(message.data) ? message.data : [message.data];
      for (const event of events) {
        if (!validEvent(event) || !markSeen(event.id)) continue;
        if (event.worldId !== core.worldId) core.nexus.ingest(event);
      }
    };
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === storageKey) ingestShared();
  };
  const onPageHide = () => flush();
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") flush();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    sync: () => { flush(); return ingestShared(); },
    stop(): void {
      if (stopped) return;
      stopped = true;
      unsubscribe();
      flush();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (channel) {
        channel.close();
        channel = null;
      }
    },
  };
}
